import axios from 'axios';

const resolveApiUrl = () => {
    // Always use local backend when frontend is opened from localhost.
    if (typeof window !== 'undefined') {
        const host = window.location.hostname;
        const isLocalHost = host === 'localhost' || host === '127.0.0.1' || host === '0.0.0.0';

        if (isLocalHost) {
            return 'http://localhost:5001/api';
        }

        // In production/staging on a real host, use same-origin API to avoid mixed-content.
        return process.env.REACT_APP_API_URL?.trim() || '/api';
    }

    // Build-time fallback when window is unavailable.
    return process.env.REACT_APP_API_URL?.trim() || '/api';
};

const API_URL = resolveApiUrl();

const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add token to requests if available
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('admin_token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Auth API
export const authAPI = {
    login: (credentials: { email: string; password: string }) =>
        api.post('/auth/login', credentials),

    verifyToken: () =>
        api.get('/auth/verify'),

    changePassword: (data: { current_password: string; new_password: string }) =>
        api.post('/auth/change-password', data),
};

// Products API
export const productsAPI = {
    getAll: (params?: { language?: string; category?: string }) =>
        api.get('/products', { params }),

    getById: (id: number, language = 'ru') =>
        api.get(`/products/${id}`, { params: { language } }),

    getWithTranslations: (id: number) =>
        api.get(`/products/${id}/translations`),

    create: (formData: FormData) =>
        api.post('/products', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        }),

    update: (id: number, formData: FormData) =>
        api.put(`/products/${id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        }),

    delete: (id: number) =>
        api.delete(`/products/${id}`),

    deleteImage: (id: number, filename: string) =>
        api.delete(`/products/${id}/image`, { data: { filename } }),

    updateDisplayOrder: (orders: Array<{ id: number; display_order: number }>) =>
        api.put('/products/order/update', { orders }),
};

// Orders API
export const ordersAPI = {
    getAll: (params?: { status?: string; page?: number; limit?: number }) =>
        api.get('/orders', { params }),

    getById: (id: number) =>
        api.get(`/orders/${id}`),

    create: (data: {
        customer_name: string;
        customer_email?: string;
        customer_phone: string;
        message?: string;
        product_id?: number;
        cart_items?: string;
    }) =>
        api.post('/orders', data),

    updateStatus: (id: number, status: string) =>
        api.put(`/orders/${id}/status`, { status }),

    delete: (id: number) =>
        api.delete(`/orders/${id}`),

    getStats: () =>
        api.get('/orders/stats'),
};

// Content API
export const contentAPI = {
    getAll: (params?: { language?: string; page?: string }) =>
        api.get('/content', { params }),

    getByKey: (key: string, language = 'ru') =>
        api.get(`/content/key/${key}`, { params: { language } }),

    getWithTranslations: (id: number) =>
        api.get(`/content/${id}/translations`),

    create: (data: {
        key_name: string;
        page: string;
        section?: string;
        translations: Record<string, string>;
    }) =>
        api.post('/content', data),

    update: (id: number, data: {
        key_name: string;
        page: string;
        section?: string;
        translations: Record<string, string>;
    }) =>
        api.put(`/content/${id}`, data),

    delete: (id: number) =>
        api.delete(`/content/${id}`),
};

// Testimonials API
export const testimonialsAPI = {
    getAll: (params?: { language?: string; active_only?: string }) =>
        api.get('/testimonials', { params }),

    getById: (id: number, language = 'ru') =>
        api.get(`/testimonials/${id}`, { params: { language } }),

    create: (data: {
        name: string;
        company?: string;
        role?: string;
        avatar?: string;
        rating: number;
        country?: string;
        display_order?: number;
        is_active?: number;
        translations: Record<string, { comment: string }>;
    }) =>
        api.post('/testimonials', data),

    update: (id: number, data: any) =>
        api.put(`/testimonials/${id}`, data),

    delete: (id: number) =>
        api.delete(`/testimonials/${id}`),

    updateDisplayOrder: (id: number, display_order: number) =>
        api.patch(`/testimonials/${id}/display-order`, { display_order }),
};

// Categories API
export const categoriesAPI = {
    getAll: (language = 'ru') =>
        api.get('/categories', { params: { language } }),

    getById: (id: number) =>
        api.get(`/categories/${id}`),

    create: (data: {
        slug: string;
        icon?: string;
        display_order?: number;
        translations: Record<string, { name: string; description?: string }>;
    }) =>
        api.post('/categories', data),

    update: (id: number, data: {
        slug: string;
        icon?: string;
        display_order?: number;
        translations: Record<string, { name: string; description?: string }>;
    }) =>
        api.put(`/categories/${id}`, data),

    delete: (id: number) =>
        api.delete(`/categories/${id}`),
};

export default api;
